/**
 * Parsed Word documents, kept across mounts under a key that names
 * immutable content (a draft's file, a report version).
 *
 * The workbench's tabs unmount the Document view, so opening it again used to
 * download and parse the file again. A cached document comes back at once,
 * with its image URLs, which live as long as the entry does. Few entries
 * (a parsed document is large), module-level, and cleared between tests
 * (`vitest.setup.mjs`).
 */

import type { DocxModel } from '@/lib/ooxml';

export interface ParsedDocx {
  model: DocxModel;
  imageUrls: Record<string, string>;
}

const MAX_DOCUMENTS = 4;

const documents = new Map<string, ParsedDocx>();

function revoke(parsed: ParsedDocx): void {
  Object.values(parsed.imageUrls).forEach((url) => URL.revokeObjectURL?.(url));
}

/** A cached document, now the most recently used. */
export function cachedDocx(key: string): ParsedDocx | undefined {
  const hit = documents.get(key);
  if (hit) {
    documents.delete(key);
    documents.set(key, hit);
  }
  return hit;
}

export function cacheDocx(key: string, parsed: ParsedDocx): void {
  const previous = documents.get(key);
  if (previous && previous !== parsed) {
    revoke(previous);
  }
  documents.delete(key);
  documents.set(key, parsed);
  while (documents.size > MAX_DOCUMENTS) {
    const [oldest, entry] = documents.entries().next().value ?? [];
    if (oldest === undefined || entry === undefined) {
      break;
    }
    documents.delete(oldest);
    revoke(entry);
  }
}

export function clearDocxCache(): void {
  documents.forEach(revoke);
  documents.clear();
}
